import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button,
  Grid, Divider, Alert, Card, CardContent, InputAdornment,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Sms as SmsIcon,
  Email as EmailIcon,
  Chat as LineIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const NotificationSettings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // 通知設定
  const [smsSettings, setSmsSettings] = useState({
    provider: 'mitake',
    username: '',
    password: '',
    sender_name: ''
  });

  const [emailSettings, setEmailSettings] = useState({
    provider: 'aws_ses',
    region: 'ap-northeast-1',
    access_key: '',
    secret_key: '',
    sender_email: '',
    sender_name: '優爾商城'
  });

  const [lineSettings, setLineSettings] = useState({
    channel_access_token: '',
    channel_secret: '',
    webhook_url: ''
  });

  // 測試對話框
  const [testDialog, setTestDialog] = useState({
    open: false,
    type: '', // sms, email, line
    data: {}
  });

  // 通知日誌
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // 追蹤日誌
  const [trackingLogs, setTrackingLogs] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 3) {
      loadNotificationLogs();
    } else if (activeTab === 4) {
      loadTrackingLogs();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/notifications');
      if (response.data.success) {
        const { sms, email, line } = response.data.settings;
        setSmsSettings(prev => ({ ...prev, ...sms }));
        setEmailSettings(prev => ({ ...prev, ...email }));
        setLineSettings(prev => ({ ...prev, ...line }));
      }
    } catch (error) {
      console.error('載入設定失敗:', error);
      toast.error('載入設定失敗');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await api.put('/settings/notifications', {
        sms: smsSettings,
        email: emailSettings,
        line: lineSettings
      });

      if (response.data.success) {
        toast.success('設定已儲存');
      } else {
        toast.error('儲存失敗');
      }
    } catch (error) {
      console.error('儲存設定失敗:', error);
      toast.error('儲存設定失敗');
    } finally {
      setSaving(false);
    }
  };

  const openTestDialog = (type) => {
    let initialData = {};
    switch (type) {
      case 'sms':
        initialData = { phone: '', message: '這是一則測試簡訊' };
        break;
      case 'email':
        initialData = { to: '', subject: '測試郵件', htmlContent: '<p>這是一封測試郵件</p>' };
        break;
      case 'line':
        initialData = { message: '這是一則測試 LINE 通知' };
        break;
    }
    setTestDialog({ open: true, type, data: initialData });
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      let response;
      const { type, data } = testDialog;

      switch (type) {
        case 'sms':
          response = await api.post('/settings/test-sms', data);
          break;
        case 'email':
          response = await api.post('/settings/test-email', data);
          break;
        case 'line':
          response = await api.post('/settings/test-line', data);
          break;
      }

      if (response.data.success) {
        toast.success(`${getTypeLabel(type)}發送成功`);
        setTestDialog({ open: false, type: '', data: {} });
        if (activeTab === 3) {
          loadNotificationLogs();
        }
      } else {
        toast.error(response.data.error || '發送失敗');
      }
    } catch (error) {
      console.error('測試發送失敗:', error);
      toast.error(error.response?.data?.error || '測試發送失敗');
    } finally {
      setTesting(false);
    }
  };

  const loadNotificationLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await api.get('/settings/notification-logs?limit=50');
      if (response.data.success) {
        setNotificationLogs(response.data.logs);
      }
    } catch (error) {
      console.error('載入通知日誌失敗:', error);
      toast.error('載入通知日誌失敗');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadTrackingLogs = async () => {
    setTrackingLoading(true);
    try {
      const response = await api.get('/settings/tracking-logs?limit=50');
      if (response.data.success) {
        setTrackingLogs(response.data.logs);
      }
    } catch (error) {
      console.error('載入追蹤日誌失敗:', error);
      toast.error('載入追蹤日誌失敗');
    } finally {
      setTrackingLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = { sms: '簡訊', email: '郵件', line: 'LINE 通知' };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      sent: 'success',
      pending: 'warning',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      sent: '已發送',
      pending: '待處理',
      failed: '失敗'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">通知與追蹤設定</Typography>
        {activeTab < 3 && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
            disabled={saving}
          >
            {saving ? '儲存中...' : '儲存設定'}
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="簡訊設定" icon={<SmsIcon />} iconPosition="start" />
          <Tab label="郵件設定" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="LINE 設定" icon={<LineIcon />} iconPosition="start" />
          <Tab label="通知日誌" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="追蹤日誌" icon={<HistoryIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* 簡訊設定 */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>三竹簡訊設定</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="服務提供商"
                  value={smsSettings.provider}
                  disabled
                  helperText="目前僅支援三竹簡訊"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="發送者名稱"
                  value={smsSettings.sender_name}
                  onChange={(e) => setSmsSettings({ ...smsSettings, sender_name: e.target.value })}
                  placeholder="優爾商城"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="帳號 (Username)"
                  value={smsSettings.username}
                  onChange={(e) => setSmsSettings({ ...smsSettings, username: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="密碼 (Password)"
                  value={smsSettings.password}
                  onChange={(e) => setSmsSettings({ ...smsSettings, password: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  請至三竹簡訊官網申請帳號：https://sms.mitake.com.tw/
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => openTestDialog('sms')}
                >
                  測試發送簡訊
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 郵件設定 */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>AWS SES 郵件設定</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="服務提供商"
                  value={emailSettings.provider}
                  disabled
                  helperText="目前僅支援 AWS SES"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="AWS Region"
                  value={emailSettings.region}
                  onChange={(e) => setEmailSettings({ ...emailSettings, region: e.target.value })}
                  placeholder="ap-northeast-1"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Access Key"
                  value={emailSettings.access_key}
                  onChange={(e) => setEmailSettings({ ...emailSettings, access_key: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Secret Key"
                  value={emailSettings.secret_key}
                  onChange={(e) => setEmailSettings({ ...emailSettings, secret_key: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="發送者郵件"
                  type="email"
                  value={emailSettings.sender_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, sender_email: e.target.value })}
                  placeholder="noreply@example.com"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="發送者名稱"
                  value={emailSettings.sender_name}
                  onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                  placeholder="優爾商城"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  請先在 AWS SES 控制台驗證發送者郵件地址
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => openTestDialog('email')}
                >
                  測試發送郵件
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* LINE 設定 */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>LINE Notify 設定</Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Channel Access Token"
                  value={lineSettings.channel_access_token}
                  onChange={(e) => setLineSettings({ ...lineSettings, channel_access_token: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="Channel Secret"
                  value={lineSettings.channel_secret}
                  onChange={(e) => setLineSettings({ ...lineSettings, channel_secret: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={lineSettings.webhook_url}
                  onChange={(e) => setLineSettings({ ...lineSettings, webhook_url: e.target.value })}
                  placeholder="https://your-domain.com/api/line/webhook"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  請至 LINE Notify 官網申請服務：https://notify-bot.line.me/
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={() => openTestDialog('line')}
                >
                  測試發送 LINE 通知
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* 通知日誌 */}
      {activeTab === 3 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">通知發送日誌</Typography>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={loadNotificationLogs}
                disabled={logsLoading}
              >
                重新整理
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {logsLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>類型</TableCell>
                      <TableCell>收件人</TableCell>
                      <TableCell>主題/內容</TableCell>
                      <TableCell>狀態</TableCell>
                      <TableCell>訂單編號</TableCell>
                      <TableCell>發送時間</TableCell>
                      <TableCell>錯誤訊息</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notificationLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          暫無通知記錄
                        </TableCell>
                      </TableRow>
                    ) : (
                      notificationLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getTypeLabel(log.notification_type)}
                              icon={
                                log.notification_type === 'sms' ? <SmsIcon /> :
                                log.notification_type === 'email' ? <EmailIcon /> :
                                <LineIcon />
                              }
                            />
                          </TableCell>
                          <TableCell>{log.recipient}</TableCell>
                          <TableCell>
                            {log.subject || log.content?.substring(0, 50)}
                            {log.content?.length > 50 && '...'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getStatusLabel(log.status)}
                              color={getStatusColor(log.status)}
                              icon={
                                log.status === 'sent' ? <SuccessIcon /> :
                                log.status === 'failed' ? <ErrorIcon /> :
                                null
                              }
                            />
                          </TableCell>
                          <TableCell>{log.order_id || '-'}</TableCell>
                          <TableCell>
                            {new Date(log.created_at).toLocaleString('zh-TW')}
                          </TableCell>
                          <TableCell>
                            {log.error_message && (
                              <Typography variant="caption" color="error">
                                {log.error_message}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* 追蹤日誌 */}
      {activeTab === 4 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">追蹤事件日誌</Typography>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={loadTrackingLogs}
                disabled={trackingLoading}
              >
                重新整理
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {trackingLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>事件類型</TableCell>
                      <TableCell>平台</TableCell>
                      <TableCell>狀態</TableCell>
                      <TableCell>訂單編號</TableCell>
                      <TableCell>用戶 ID</TableCell>
                      <TableCell>IP 地址</TableCell>
                      <TableCell>發送時間</TableCell>
                      <TableCell>錯誤訊息</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackingLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          暫無追蹤記錄
                        </TableCell>
                      </TableRow>
                    ) : (
                      trackingLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Chip size="small" label={log.event_type} />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={log.platform} color="primary" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getStatusLabel(log.status)}
                              color={getStatusColor(log.status)}
                            />
                          </TableCell>
                          <TableCell>{log.order_id || '-'}</TableCell>
                          <TableCell>{log.user_id || '-'}</TableCell>
                          <TableCell>{log.ip_address || '-'}</TableCell>
                          <TableCell>
                            {new Date(log.created_at).toLocaleString('zh-TW')}
                          </TableCell>
                          <TableCell>
                            {log.error_message && (
                              <Typography variant="caption" color="error">
                                {log.error_message}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* 測試對話框 */}
      <Dialog open={testDialog.open} onClose={() => setTestDialog({ open: false, type: '', data: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>測試{getTypeLabel(testDialog.type)}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {testDialog.type === 'sms' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="手機號碼"
                    value={testDialog.data.phone || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, phone: e.target.value } })}
                    placeholder="0912345678"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="簡訊內容"
                    value={testDialog.data.message || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, message: e.target.value } })}
                    required
                  />
                </Grid>
              </Grid>
            )}

            {testDialog.type === 'email' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="email"
                    label="收件人郵件"
                    value={testDialog.data.to || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, to: e.target.value } })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="郵件主題"
                    value={testDialog.data.subject || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, subject: e.target.value } })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="郵件內容 (HTML)"
                    value={testDialog.data.htmlContent || ''}
                    onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, htmlContent: e.target.value } })}
                  />
                </Grid>
              </Grid>
            )}

            {testDialog.type === 'line' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="通知內容"
                value={testDialog.data.message || ''}
                onChange={(e) => setTestDialog({ ...testDialog, data: { ...testDialog.data, message: e.target.value } })}
                required
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog({ open: false, type: '', data: {} })}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleTest}
            disabled={testing}
            startIcon={<SendIcon />}
          >
            {testing ? '發送中...' : '發送'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettings;
